/**
 * prePublishGate.ts — module-level registry for the universal pre-publish
 * metadata warning gate.
 *
 * Design contract (fail-open, never a dead-end):
 *  - `registerPrePublishConfirm(handler)` installs the app-root dialog handler
 *    (see PrePublishGateProvider). Returns an unregister fn.
 *  - `confirmPublish(items)` is called from the publish service layer BEFORE the
 *    network write. If any item has violations AND a handler is registered it
 *    resolves to the user's choice plus optional acknowledgment; otherwise it
 *    FAILS OPEN (resolves true) so programmatic publishes (tests, pollers, SSR,
 *    un-mounted app) never break.
 *  - On "Go back" the service throws `PublishCancelledError`; every publish
 *    caller's error path early-returns on it so cancelling shows no error toast.
 *
 * Brand name (rows 4/12/18/26) is external per-tenant data. It is resolved
 * best-effort and SYNCHRONOUSLY via a resolver the provider installs
 * (`registerBrandNameResolver`), reading the react-query brand-bible cache.
 * When unavailable, brand rules silently skip (evaluator receives no brandName).
 */

import { getHumanQaState, humanQaReasonsText } from './humanQa';
import { humanVerifyViolations, unresolvedHumanVerifyItems, type HumanVerifyItem } from './humanVerify';
import { evaluateMetadataCriteria, type MetadataTier, type MetadataViolation } from './metadataCriteria';
import { readPostgenEvidence } from '../components/editor-chrome/rail/postgenEvidence';
import { missingAltViolations, type GateImage } from './missingAlt';
import { featuredImageMissingViolations } from './featuredImageGate';

export interface PublishConfirmationRule {
  id: string;
  tier: MetadataTier;
  message: string;
}

export interface PublishConfirmationPayload {
  confirmed_by: string;
  confirmed_at: string;
  rules: PublishConfirmationRule[];
  justification?: string;
  reviewer?: string;
}

export interface PublishConfirmResult {
  proceed: boolean;
  confirmation?: PublishConfirmationPayload;
}

/** Editable metadata field values for the FIX section of the dialog. */
export interface EditableFieldValues {
  meta_title?: string | null;
  meta_description?: string | null;
  url_slug?: string | null;
  title?: string | null;
}

/** Context needed to live-re-evaluate metadata criteria as the reviewer types. */
export interface GateEvalContext {
  keyword?: string | null;
  brandName?: string | null;
  focusKeyword?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  twitterTitle?: string | null;
  twitterDescription?: string | null;
}

/** One article's worth of violations for the confirm dialog. */
export interface GateItem {
  /** Content id (for keys / debugging); optional for ad-hoc evaluations. */
  contentId?: string;
  /** Human label shown as the article heading in the dialog. */
  title?: string;
  /** The URL slug being published (used by async slug-collision enrichment). */
  urlSlug?: string;
  violations: MetadataViolation[];
  /** Current values of editable metadata fields (for the FIX section inputs). */
  fieldValues?: EditableFieldValues;
  /** Unresolved human-verify items (for the CONFIRM section). */
  humanVerifyItems?: HumanVerifyItem[];
  /** Context for live re-evaluation of metadata criteria. */
  evalContext?: GateEvalContext;
  /** True while a whole-run metadata generation is in flight — the per-field
   *  Suggest buttons are disabled so the two can't race. */
  metadataGenerating?: boolean;
}

/**
 * Thrown by the publish service when the user chooses "Go back" in the gate.
 * Callers detect it (via `isPublishCancelledError`) and abort quietly — no toast.
 */
export class PublishCancelledError extends Error {
  readonly cancelled = true as const;
  constructor(message = 'Publish cancelled by the pre-publish metadata gate') {
    super(message);
    this.name = 'PublishCancelledError';
  }
}

/** Robust across bundling / instanceof pitfalls. */
export function isPublishCancelledError(err: unknown): err is PublishCancelledError {
  return (
    err instanceof PublishCancelledError ||
    (typeof err === 'object' &&
      err !== null &&
      (err as { name?: unknown }).name === 'PublishCancelledError')
  );
}

// ---------------------------------------------------------------------------
// Confirm handler registry
// ---------------------------------------------------------------------------
type ConfirmHandler = (items: GateItem[]) => Promise<boolean | PublishConfirmResult>;
let confirmHandler: ConfirmHandler | null = null;

/** Install the dialog handler (call once, from the app-root provider). */
export function registerPrePublishConfirm(handler: ConfirmHandler): () => void {
  confirmHandler = handler;
  return () => {
    if (confirmHandler === handler) confirmHandler = null;
  };
}

/** Testing/inspection helper. */
export function hasPrePublishConfirmHandler(): boolean {
  return confirmHandler !== null;
}

/**
 * Gate a publish. Resolves true to proceed, false to abort.
 *  - No item has any violation → resolve true, dialog is NOT shown.
 *  - No handler registered → resolve true (fail-open).
 *  - Handler throws → resolve true (never break a publish over a dialog bug).
 */
function normalizeConfirmResult(result: boolean | PublishConfirmResult): PublishConfirmResult {
  if (typeof result === 'boolean') return { proceed: result };
  return result;
}

export async function confirmPublishWithAcknowledgement(items: GateItem[]): Promise<PublishConfirmResult> {
  // Async enrichment: slug-collision warning (rule 34). Fail-open: no-op when
  // no checker is registered, and any error/timeout is swallowed per item.
  await enrichWithSlugCollisions(items);

  const flagged = items.filter((item) => item.violations.length > 0);
  if (flagged.length === 0) return { proceed: true };
  if (!confirmHandler) return { proceed: true };
  try {
    return normalizeConfirmResult(await confirmHandler(flagged));
  } catch {
    return { proceed: true };
  }
}

export async function confirmPublish(items: GateItem[]): Promise<boolean> {
  return (await confirmPublishWithAcknowledgement(items)).proceed;
}

// ---------------------------------------------------------------------------
// Brand-name resolver registry (best-effort, synchronous)
// ---------------------------------------------------------------------------
type BrandNameResolver = (tenantId?: string | null) => string | null | undefined;
let brandNameResolver: BrandNameResolver | null = null;

/** Install a synchronous best-effort brand-name lookup (from the provider). */
export function registerBrandNameResolver(fn: BrandNameResolver): () => void {
  brandNameResolver = fn;
  return () => {
    if (brandNameResolver === fn) brandNameResolver = null;
  };
}

/** Resolve the tenant brand name, or null when unavailable. Never throws. */
export function resolveBrandName(tenantId?: string | null): string | null {
  if (!brandNameResolver) return null;
  try {
    return brandNameResolver(tenantId) ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Slug-collision checker registry (best-effort, async, fail-open)
// ---------------------------------------------------------------------------

/** A single conflicting page/article returned by the backend collision check. */
export interface SlugConflict {
  url: string | null;
  slug: string;
  /** Backend-owned vocabulary ('blog' | 'page' today); display-only here. */
  kind: string;
  title?: string | null;
  source: string;
}

/** Response shape from GET /blog-creation/slug-collision. */
export interface SlugCollisionResult {
  collides: boolean;
  conflicts: SlugConflict[];
}

/**
 * Async slug-collision checker. Installed by the app-root provider; receives
 * the slug + optional content id to exclude, and returns whether that slug is
 * already owned by a different published page/article. The tenant is captured
 * in the closure by the provider (same pattern as the brand-name resolver).
 */
export type SlugCollisionChecker = (
  slug: string,
  excludeContentId?: string,
) => Promise<SlugCollisionResult>;

let slugCollisionChecker: SlugCollisionChecker | null = null;

/** Hard cap on the enrichment step so publish is never delayed indefinitely. */
const SLUG_COLLISION_TIMEOUT_MS = 4000;

/** Install the async slug-collision checker (call once, from the provider). */
export function registerSlugCollisionChecker(fn: SlugCollisionChecker): () => void {
  slugCollisionChecker = fn;
  return () => {
    if (slugCollisionChecker === fn) slugCollisionChecker = null;
  };
}

/**
 * Enrich gate items with slug-collision violations (rule 34 / `slug_collision`).
 *
 * Contract (fail-open):
 *  - No checker registered → no-op (tests / SSR / un-mounted app).
 *  - Checker throws or is slow past the timeout → skip that item.
 *  - Mutates `item.violations` in place (items are ephemeral per publish).
 *
 * The warning is always tier `warn` (never block) — house convention for
 * slug collisions is warn-but-allow.
 */
async function enrichWithSlugCollisions(items: GateItem[]): Promise<void> {
  if (!slugCollisionChecker) return;

  const candidates = items.filter(
    (item) => item.urlSlug && item.urlSlug.trim().length > 0,
  );
  if (candidates.length === 0) return;

  await Promise.all(
    candidates.map(async (item) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const slug = item.urlSlug!.trim();
        const timeout = new Promise<never>((_, reject) => {
          timer = setTimeout(
            () => reject(new Error('slug-collision timeout')),
            SLUG_COLLISION_TIMEOUT_MS,
          );
        });
        const result = await Promise.race([
          slugCollisionChecker!(slug, item.contentId),
          timeout,
        ]);
        if (result.collides && result.conflicts.length > 0) {
          const urls = result.conflicts
            .map((c) => c.url || c.slug)
            .filter(Boolean)
            .slice(0, 3)
            .join(', ');
          item.violations.push({
            ruleId: 'slug_collision',
            field: 'url_slug',
            tier: 'warn',
            severity: 'warn',
            category: 'uniqueness',
            message: urls
              ? `URL slug "${slug}" collides with an existing published page (${urls}). WordPress will silently append "-2" — fix or confirm.`
              : `URL slug "${slug}" collides with an existing published page. WordPress will silently append "-2" — fix or confirm.`,
            sourceNote: 'row 34 (slug-collision pre-publish)',
          });
        }
      } catch {
        // fail-open: no warning added on error/timeout
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    }),
  );
}

// ---------------------------------------------------------------------------
// ContentDetail -> GateItem mapping (shared by service.publish / batchPublish /
// the content-planner schedule flow so the field mapping lives in one place).
// ---------------------------------------------------------------------------

/** Structural subset of ContentDetail the gate reads (avoids a type import cycle). */
export interface GateableContent {
  id?: string;
  status?: string | null;
  /** Whole-run metadata generation state (queued/running → Suggest disabled). */
  metadata_generation_status?: string | null;
  title?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  url_slug?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  twitter_title?: string | null;
  twitter_description?: string | null;
  keyword?: string | null;
  focus_keyword?: string | null;
  /** Editor "Use as OG image" override. */
  og_image_url?: string | null;
  /** WordPress media id designated as the featured image in the editor. */
  featured_image_id?: number | null;
  human_verify?: unknown;
  generation_metadata?: {
    human_qa?: unknown;
    human_verify?: unknown;
  } & Record<string, unknown>;
  /**
   * Optional article images for missing-alt detection. Callers that have the
   * TipTap doc (e.g. UniversalEditorPage) pass the image list so the gate can
   * fold accessibility violations in. Omitted when the caller has no doc.
   */
  images?: readonly GateImage[];
}

/** Evaluate one content row into a GateItem, resolving brand best-effort. */
export function buildGateItem(content: GateableContent, tenantId?: string | null): GateItem {
  const brandName = resolveBrandName(tenantId);
  const violations = evaluateMetadataCriteria({
    metaTitle: content.meta_title,
    metaDescription: content.meta_description,
    title: content.title,
    urlSlug: content.url_slug,
    ogTitle: content.og_title,
    ogDescription: content.og_description,
    twitterTitle: content.twitter_title,
    twitterDescription: content.twitter_description,
    keyword: content.keyword,
    focusKeyword: content.focus_keyword,
    brandName,
  });
  // evaluateMetadataCriteria already tagged every contract violation with
  // category 'contract' via normalizeViolation. The rows appended below set
  // their own category explicitly.
  if (content.status === 'failed') {
    violations.push({
      ruleId: 'backend_status_failed',
      field: 'status',
      tier: 'block',
      severity: 'block',
      category: 'pipeline',
      message: "Backend status is 'failed'; quality gate failure or generation error needs human sign-off.",
      sourceNote: 'human-qa gate',
    });
  }
  const humanQa = getHumanQaState(content);
  if (humanQa?.required === true && humanQa.status !== 'approved') {
    violations.push({
      ruleId: 'human_qa_pending',
      field: 'human_qa',
      tier: 'block',
      severity: 'block',
      category: 'human',
      message: `Pending human QA sign-off (${humanQaReasonsText(humanQa)}).`,
      sourceNote: 'generation_metadata.human_qa',
    });
  }
  violations.push(
    ...humanVerifyViolations(content).map((v) => ({ ...v, category: 'human' as const })),
  );

  // ── Provenance fold ────────────────────────────────────────────────────
  // readPostgenEvidence reads generation_metadata and returns per-field
  // provenance. For each field with source 'fallback' (template default or
  // failed run), add a warn-tier provenance violation — UNLESS a contract
  // violation already exists for that field (the field is already represented
  // and summarizeGateIssues dedups per field, so we avoid a duplicate row).
  // readPostgenEvidence takes ContentDetail; GateableContent is a structural
  // subset carrying every field the reader touches, so the cast is safe.
  try {
    const evidence = readPostgenEvidence(
      content as unknown as Parameters<typeof readPostgenEvidence>[0],
    );
    if (evidence && evidence.fieldStates) {
      const contractFields = new Set(
        violations
          .filter((v) => v.category === 'contract')
          .map((v) => v.field),
      );
      for (const [field, state] of Object.entries(evidence.fieldStates)) {
        if (state.source !== 'fallback') continue;
        if (contractFields.has(field)) continue;
        violations.push({
          ruleId: 'provenance_fallback',
          field,
          tier: 'warn',
          severity: 'warn',
          category: 'provenance',
          message: `This ${field} is a template default — regenerate or edit.`,
          sourceNote: 'postgen evidence: fallback_fired',
        });
      }
    }
  } catch {
    // fail-open: provenance is informational, never break the gate
  }

  // ── Accessibility fold ─────────────────────────────────────────────────
  if (content.images && content.images.length > 0) {
    violations.push(...missingAltViolations(content.images));
  }

  // ── Featured/OG image fold (info-tier, never blocking) ─────────────────
  violations.push(
    ...featuredImageMissingViolations({
      ogImageUrl: content.og_image_url,
      featuredImageId: content.featured_image_id,
      generationMetadata: content.generation_metadata,
    }),
  );

  const fieldValues: EditableFieldValues = {
    meta_title: content.meta_title ?? null,
    meta_description: content.meta_description ?? null,
    url_slug: content.url_slug ?? null,
    title: content.title ?? null,
  };
  const humanVerifyItems = unresolvedHumanVerifyItems(content);
  const evalContext: GateEvalContext = {
    keyword: content.keyword ?? null,
    brandName,
    focusKeyword: content.focus_keyword ?? null,
    ogTitle: content.og_title ?? null,
    ogDescription: content.og_description ?? null,
    twitterTitle: content.twitter_title ?? null,
    twitterDescription: content.twitter_description ?? null,
  };
  const generationState = content.metadata_generation_status ?? null;
  return {
    contentId: content.id,
    title: content.title ?? undefined,
    urlSlug: content.url_slug ?? undefined,
    violations,
    fieldValues,
    humanVerifyItems,
    evalContext,
    metadataGenerating:
      generationState === 'queued' || generationState === 'running',
  };
}

/**
 * Convenience: evaluate one content row and run the gate. Throws
 * `PublishCancelledError` if the user aborts. Used by the publish service.
 */
export async function gateContentPublish(
  content: GateableContent,
  tenantId?: string | null,
): Promise<void> {
  const ok = await confirmPublish([buildGateItem(content, tenantId)]);
  if (!ok) throw new PublishCancelledError();
}
